import { useMemo, useRef } from 'react';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';
import { getHomeData } from '../home/data';
import { motion } from 'framer-motion';

function renderTitleWithHighlight(item) {
  const line1 = String(item.titleLine1 || '').trim();
  const line2 = String(item.titleLine2 || '').trim();
  if (line1 || line2) {
    return (
      <>
        <span>{line1 || String(item.title || '')}</span>
        {line2 ? <span className="font-bold text-[var(--dash-other)]"> {line2}</span> : null}
      </>
    );
  }

  const title = String(item.title || '');
  const highlight = String(item.highlight || '');
  const idx = title.toLowerCase().indexOf(highlight.toLowerCase());
  if (idx === -1 || !highlight) {
    return <span className="text-white">{title}</span>;
  }
  const before = title.slice(0, idx);
  const marked = title.slice(idx, idx + highlight.length);
  const after = title.slice(idx + highlight.length);
  return (
    <>
      <span className="text-white">{before}</span>
      <span className="font-bold text-[var(--dash-main)]">{marked}</span>
      <span className="text-white">{after}</span>
    </>
  );
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};


export default function ServicesList() {
  const { language, t } = useI18n();
  const { homeServices } = getHomeData(language);
  const longDescriptions = t('servicesPage.items');
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.08) 0%, transparent 40%)`;
      }
    });
  };

  const services = useMemo(() => {
    const list = Array.isArray(homeServices) ? [...homeServices] : [];
    if (!list.length) return list;
    if (Array.isArray(longDescriptions)) {
      list.forEach((item, idx) => {
        const override = longDescriptions[idx];
        if (override && override.description) {
          list[idx] = { ...item, description: override.description };
        }
      });
    }
    return list.filter((_, index) => index !== 2);
  }, [homeServices, longDescriptions]);

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-blacker)] py-24"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute left-0 top-0 h-full w-full bg-[radial-gradient(circle_at_15%_25%,rgba(255,93,16,0.08)_0%,transparent_40%)]" />
      </div>
      <div className="pointer-events-none absolute inset-0 z-1">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{
            backgroundImage: 'radial-gradient(circle at 45% 35%, rgba(255,93,16,0.08) 0%, transparent 40%)',
          }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[600px] w-[600px] translate-x-1/4 -translate-y-1/2 rounded-full bg-[#ff5d10]/5 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[500px] w-[500px] -translate-x-1/4 translate-y-1/2 rounded-full bg-blue-500/5 blur-[100px]" />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(#ffffff 0.5px, transparent 0.5px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <motion.div
          className="mb-12"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
          key={language}
        >
          <motion.div variants={itemVariants}>
            <AnimatedEyebrow className="mb-4">{t('home.services.eyebrow')}</AnimatedEyebrow>
          </motion.div>
          <motion.h1 variants={itemVariants} className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
            <span className="block">{t('home.services.titleLine1')}</span>
            <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
              {t('home.services.titleLine2')}
            </span>
          </motion.h1>
        </motion.div>

        <motion.div
          className="space-y-16"
          key={`${language}-services-list`}
        >
          {services.map((service, index) => {
            const isEven = index % 2 !== 0;
            const isLast = index === services.length - 1;
            return (
              <motion.article
                key={`${service.title}-${index}`}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-120px' }}
                className={`group relative flex w-full flex-col justify-between items-stretch gap-8 pb-12 md:flex-col md:gap-12 lg:flex-row ${
                  isEven ? 'lg:flex-row-reverse' : ''
                } ${isLast ? '' : 'border-b border-white/10'}`}
              >
                <motion.div className="w-full lg:w-2/5" variants={itemVariants}>
                  <div className="relative h-full overflow-hidden rounded-3xl bg-[var(--dash-black)]">
                    <ImageOnlySkeletonLoader
                      src={service.image}
                      mode="native"
                      alt={service.title}
                      containerClassName="h-full w-full"
                      imgClassName="h-full w-full object-cover grayscale opacity-70 transition-all duration-1000 group-hover:scale-105 group-hover:grayscale-0 group-hover:opacity-100"
                    />
                    <div className="absolute left-5 top-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-[var(--dash-other)] group-hover:bg-[var(--dash-main)] text-sm font-bold uppercase tracking-[0.15em] text-[var(--dash-black)] group-hover:text-white transition-all duration-300">
                      0{index + 1}
                    </div>
                  </div>
                </motion.div>

                <motion.div className="w-full lg:w-[60%] " variants={itemVariants}>
                  <div className="h-full rounded-2xl border border-white/5 bg-[rgb(var(--dash-blacker-rgb)/0.2)] p-6 md:p-10">
                    <motion.h3 className="mb-4 flex items-center gap-3 text-3xl font-light uppercase tracking-[0.02em] text-white md:text-4xl" variants={itemVariants}>
                      <span className="h-3 w-3 rounded-sm bg-[var(--dash-main)]" />
                      <span>{renderTitleWithHighlight(service)}</span>
                    </motion.h3>
                    <motion.div className="space-y-3 text-[15px] leading-7 text-slate-400 md:text-[15px]" variants={containerVariants}>
                      {String(service.description || '')
                        .split(/\n\s*\n/)
                        .map((paragraph, idx) => {
                          const match = paragraph.match(/^([^:]{1,40}:)\s*(.*)$/);
                          if (!match) {
                            return <motion.p key={`${service.title}-${idx}`} variants={itemVariants}>{paragraph}</motion.p>;
                          }
                          const [, lead, rest] = match;
                          const cleanLead = lead.replace(/:\s*$/, '');
                          return (
                            <motion.p key={`${service.title}-${idx}`} variants={itemVariants}>
                              <span
                                className={`block mr-2 mb-2 w-fit items-center rounded-md bg-[var(--dash-main)] px-2 py-1 text-xs font-semibold uppercase tracking-[0.08em] text-white ${
                                  idx === 0 ? 'mt-5' : ''
                                }`}
                              >
                                {cleanLead}
                              </span>
                              {rest}
                            </motion.p>
                          );
                        })}
                    </motion.div>
                  </div>
                </motion.div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
